import type { Session } from "@supabase/supabase-js"
import { useNavigate, useSearch } from "@tanstack/react-router"
import React, { useEffect, useState } from "react"

import { getSafeAuthRedirectFromCandidates } from "../lib/authRedirect"
import { supabase } from "../lib/supabase"

export default function AuthCallbackScreen() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const navigate = useNavigate()
  const search = useSearch({ from: "/auth/callback" })

  useEffect(() => {
    console.log("🔄 Auth callback screen mounted")
    console.log("🌍 Current URL:", window.location.href)
    console.log("🔗 URL Hash:", window.location.hash)

    let authSubscription: ReturnType<typeof supabase.auth.onAuthStateChange> | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const handleRedirect = (session: Session | null) => {
      if (session) {
        console.log("✅ User signed in via callback, redirecting...")

        // Clear timeout since we got the session
        if (timeoutId) {
          clearTimeout(timeoutId)
        }

        // Successfully authenticated, redirect to stored path or home
        const redirectTo = getSafeAuthRedirectFromCandidates(search.redirect, localStorage.getItem("authRedirect"))
        localStorage.removeItem("authRedirect")

        console.log("🎯 Redirecting to:", redirectTo)
        window.location.replace(redirectTo)
      }
    }

    const handleAuthStateChange = (event: string, session: Session | null) => {
      console.log("🔐 Auth state change in callback:", { event, hasSession: !!session })

      if (event === "SIGNED_IN" && session) {
        handleRedirect(session)
      }
    }

    // Check if there's a session in the URL hash immediately
    if (window.location.hash?.includes("access_token")) {
      console.log("🔍 Found access token in URL, processing session...")

      // Use Supabase's session parsing
      supabase.auth
        .getSession()
        .then(({ data, error }) => {
          if (error) {
            console.error("❌ Session parsing failed:", error)
            // Continue with normal flow
          } else if (data.session) {
            console.log("✅ Successfully parsed session from URL")
            handleRedirect(data.session)
            return // Exit early if successful
          }

          // If immediate parsing didn't work, continue with listener
          console.log("🔄 Immediate parsing failed, waiting for auth state change...")
        })
        .catch((err) => {
          console.error("❌ Session parsing error:", err)
          // Continue with normal flow
        })
    }

    // Listen for auth state changes as backup
    authSubscription = supabase.auth.onAuthStateChange(handleAuthStateChange)

    // Set timeout to prevent infinite waiting - reduced to 5 seconds
    timeoutId = setTimeout(() => {
      console.log("⏰ Auth callback timeout, checking current session...")

      // Try to get current session as fallback
      supabase.auth
        .getSession()
        .then(({ data, error }) => {
          if (error) {
            console.error("❌ Session check failed:", error)
            setError("Authentication failed. Please try again.")
            setLoading(false)
            return
          }

          if (data.session) {
            console.log("✅ Found existing session on timeout")
            handleRedirect(data.session)
          } else {
            console.log("❌ No session found on timeout")
            localStorage.removeItem("authRedirect")
            setError("Authentication timed out. Please try again.")
            setLoading(false)
          }
        })
        .catch((err: unknown) => {
          console.error("❌ Session check error:", err)
          setError("Authentication failed. Please try again.")
          setLoading(false)
        })
    }, 5000) // 5 second timeout

    // Cleanup function
    return () => {
      if (authSubscription?.data?.subscription) {
        authSubscription.data.subscription.unsubscribe()
      }
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [search.redirect])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-foregroundx">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 ">Completing sign in...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-foregroundx">
        <div className="text-center">
          <div className="rounded-md bg-red-50 p-4 max-w-md">
            <div className="text-sm text-destructive">
              <p className="font-medium">Authentication Error</p>
              <p className="mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={() => navigate({ to: "/" })}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  return null
}
